import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Users, GripVertical } from "lucide-react";
import { api } from "@/lib/api";

const inputCls = "w-full bg-[#141414] border border-[#2a2a2a] px-3 py-2 text-[13px] text-[#e8e8e8] rounded focus:outline-none focus:border-[#4deb7a] transition-colors";

const parsePct = (v) => {
  const n = parseFloat(String(v || "").replace(/[^0-9.]/g, ""));
  return isNaN(n) ? 0 : n;
};

function EntryRow({ e, isAdmin, editing, onEdit, onChange, onSave, onCancel, onDelete, fromTeam, dragProps, dragging, pool, note }) {
  if (editing) {
    return (
      <div className="grid grid-cols-[1.5fr_1fr_90px_1.4fr_64px] gap-3 items-center px-4 py-2.5 border-b border-[#141414] bg-[#0e0e0e]">
        <input value={e.name} onChange={(ev) => onChange({ ...e, name: ev.target.value })} placeholder="Name" className={inputCls} data-testid="captable-name" />
        <input value={e.role} onChange={(ev) => onChange({ ...e, role: ev.target.value })} placeholder="Role" className={inputCls} data-testid="captable-role" />
        <input value={e.equity} onChange={(ev) => onChange({ ...e, equity: ev.target.value })} placeholder="Equity %" className={inputCls} data-testid="captable-equity" />
        <input value={e.terms} onChange={(ev) => onChange({ ...e, terms: ev.target.value })} placeholder="Terms" className={inputCls} data-testid="captable-terms" />
        <div className="flex items-center gap-2">
          <button onClick={onSave} className="text-[#4deb7a] text-[12px]" data-testid="captable-save">Save</button>
          <button onClick={onCancel} className="text-[#8f8f8f]"><X className="h-4 w-4" /></button>
        </div>
      </div>
    );
  }
  const canDrag = isAdmin && !fromTeam && !!dragProps;
  return (
    <div {...(dragProps || {})} className={`group grid grid-cols-[1.5fr_1fr_90px_1.4fr_64px] gap-3 items-center px-4 py-3 border-b border-[#141414] hover:bg-[#0e0e0e] transition-opacity ${dragging ? "opacity-40" : ""} ${pool ? "bg-[rgba(77,235,122,0.02)]" : ""}`} data-testid={pool ? "captable-pool-row" : `captable-row-${e.id}`}>
      <div className={`text-[13px] text-[#f0f0f0] truncate flex items-center gap-2 ${fromTeam ? "pl-5" : ""}`}>
        {canDrag && <GripVertical data-testid={`captable-drag-${e.id}`} className="h-3.5 w-3.5 text-[#5a5a5a] cursor-grab shrink-0 md:opacity-0 md:group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />}
        {e.name || "—"}
        {pool && <span className="font-mono-dm text-[8px] uppercase tracking-wider text-[#4deb7a] border border-[rgba(77,235,122,0.25)] px-1.5 py-0.5 rounded-sm">Pool</span>}
        {fromTeam && <span className="font-mono-dm text-[8px] uppercase tracking-wider text-[#8f8f8f] border border-[#2a2a2a] px-1.5 py-0.5 rounded-sm">Team</span>}
      </div>
      <span className="text-[12px] text-[#d4d4d4] truncate">{e.role || "—"}</span>
      <span className="font-mono-dm text-[12px] text-[#4deb7a]">{e.equity || "—"}</span>
      <div className="min-w-0">
        <div className="text-[12px] text-[#d4d4d4] truncate">{e.terms || "—"}</div>
        {note && <div className="font-mono-dm text-[10px] text-[#8f8f8f] truncate mt-0.5" data-testid="captable-pool-note">{note}</div>}
      </div>
      <div className="flex items-center gap-1.5">
        {isAdmin && !fromTeam && (
          <div className="md:opacity-0 md:group-hover:opacity-100 transition-opacity flex items-center gap-1.5">
            <button onClick={onEdit} className="text-[#8f8f8f] hover:text-[#e8e8e8]"><Pencil className="h-3.5 w-3.5" /></button>
            {!pool && <button onClick={onDelete} className="text-[#8f8f8f] hover:text-[#c83232]"><Trash2 className="h-3.5 w-3.5" /></button>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CapTable({ section, isAdmin, onChanged }) {
  const data = section.captable || { team_rows: [], manual_rows: [] };
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [adding, setAdding] = useState(false);
  const splitRows = (rows) => (rows || []).filter((r) => !r.is_pool);
  const [manualRows, setManualRows] = useState(splitRows(data.manual_rows));
  const [dragId, setDragId] = useState(null);
  useEffect(() => { setManualRows(splitRows(data.manual_rows)); }, [data.manual_rows]);

  const teamRows = data.team_rows || [];
  const poolRow = (data.manual_rows || []).find((r) => r.is_pool);
  const poolPct = parsePct(poolRow?.equity);
  const teamTotal = teamRows.reduce((a, r) => a + parsePct(r.equity), 0);
  const othersTotal = poolPct + manualRows.reduce((a, r) => a + parsePct(r.equity), 0);
  const retained = Math.max(0, 100 - othersTotal);
  const fmtPct = (n) => `${n.toFixed(2).replace(/\.?0+$/, "")}%`;

  const startAdd = () => { setDraft({ name: "", role: "", equity: "", terms: "" }); setAdding(true); setEditingId(null); };
  const startEdit = (r) => { setDraft({ ...r }); setEditingId(r.id); setAdding(false); };
  const cancel = () => { setDraft(null); setEditingId(null); setAdding(false); };

  const saveNew = async () => {
    try { await api.post(`/admin/sections/${section.id}/captable`, draft); toast.success("Row added"); cancel(); onChanged?.(); }
    catch { toast.error("Failed to add row"); }
  };
  const saveEdit = async () => {
    try { await api.put(`/admin/captable/${editingId}`, draft); toast.success("Row updated"); cancel(); onChanged?.(); }
    catch { toast.error("Failed to update"); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/captable/${id}`); toast.success("Row removed"); onChanged?.(); }
    catch { toast.error("Failed to remove row"); }
  };

  const handleDrop = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const arr = [...manualRows];
    const from = arr.findIndex((r) => r.id === dragId);
    const to = arr.findIndex((r) => r.id === targetId);
    if (from === -1 || to === -1) { setDragId(null); return; }
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setManualRows(arr);
    setDragId(null);
    try { await api.put(`/admin/captable/reorder`, { order: arr.map((r) => r.id) }); onChanged?.(); }
    catch { toast.error("Failed to save order"); setManualRows(splitRows(data.manual_rows)); }
  };

  return (
    <div data-testid="captable-section">
      {isAdmin && (
        <div className="mb-5">
          <button data-testid="captable-add-btn" onClick={startAdd} className="inline-flex items-center gap-2 bg-[#4deb7a] hover:bg-[#3dd869] text-[#080808] text-[12px] font-medium px-4 py-2.5 rounded transition-colors">
            <Plus className="h-4 w-4" strokeWidth={1.5} /> Add row
          </button>
        </div>
      )}

      <div className="border border-[#222] rounded-md overflow-hidden overflow-x-auto">
        <div className="min-w-[640px]">
          <div className="grid grid-cols-[1.5fr_1fr_90px_1.4fr_64px] gap-3 font-mono-dm uppercase tracking-[0.1em] text-[9px] text-[#8f8f8f] px-4 py-2.5 border-b border-[#222] bg-[#0b0b0b]">
            <span>Name</span><span>Role</span><span>Equity</span><span>Terms</span><span></span>
          </div>

          {/* Retained (You) — starts at 100%, deducts with every entry */}
          <div className="grid grid-cols-[1.5fr_1fr_90px_1.4fr_64px] gap-3 items-center px-4 py-3 border-b border-[#141414] bg-[rgba(77,235,122,0.04)]" data-testid="captable-retained">
            <div className="text-[13px] text-[#f0f0f0] truncate flex items-center gap-2">
              You (Founder)
              <span className="font-mono-dm text-[8px] uppercase tracking-wider text-[#4deb7a] border border-[rgba(77,235,122,0.25)] px-1.5 py-0.5 rounded-sm">Retained</span>
            </div>
            <span className="text-[12px] text-[#d4d4d4] truncate">Founder</span>
            <span className="font-mono-dm text-[12px] text-[#4deb7a]">{fmtPct(retained)}</span>
            <span className="text-[12px] text-[#8f8f8f] truncate">Remaining after allocations</span>
            <span></span>
          </div>

          {poolRow && (
            <EntryRow e={editingId === poolRow.id ? draft : poolRow} isAdmin={isAdmin} pool
              editing={editingId === poolRow.id}
              note={`${fmtPct(teamTotal)} allocated · ${fmtPct(Math.max(0, poolPct - teamTotal))} available${isAdmin && teamTotal > poolPct ? " · ⚠ exceeds pool" : ""}`}
              onEdit={() => startEdit(poolRow)} onChange={setDraft} onSave={saveEdit} onCancel={cancel} />
          )}

          {teamRows.length === 0 && manualRows.length === 0 && !adding && !poolRow && (
            <div className="px-4 py-6 text-center border-b border-[#141414]">
              <div className="text-[12px] text-[#8f8f8f]">{isAdmin ? "You currently retain 100%. Assign equity to team members or add a row to allocate." : "Ownership allocations will appear here."}</div>
            </div>
          )}

          {teamRows.map((r) => <EntryRow key={r.id} e={r} isAdmin={isAdmin} fromTeam editing={false} />)}
          {manualRows.map((r) => (
            <EntryRow key={r.id} e={editingId === r.id ? draft : r} isAdmin={isAdmin}
              editing={editingId === r.id}
              dragging={dragId === r.id}
              dragProps={isAdmin && editingId !== r.id ? {
                draggable: true,
                onDragStart: () => setDragId(r.id),
                onDragOver: (ev) => ev.preventDefault(),
                onDrop: () => handleDrop(r.id),
              } : null}
              onEdit={() => startEdit(r)} onChange={setDraft} onSave={saveEdit} onCancel={cancel} onDelete={() => del(r.id)} />
          ))}
          {adding && (
            <EntryRow e={draft} isAdmin editing onChange={setDraft} onSave={saveNew} onCancel={cancel} />
          )}

          <div className="grid grid-cols-[1.5fr_1fr_90px_1.4fr_64px] gap-3 items-center px-4 py-3 bg-[#0b0b0b] border-t border-[#222]">
            <span className="text-[13px] text-[#f0f0f0] font-medium">Total</span>
            <span></span>
            <span className="font-mono-dm text-[13px] text-[#4deb7a]">{fmtPct(retained + othersTotal)}</span>
            <span className="text-[11px] text-[#8f8f8f]">{othersTotal > 100 ? "⚠ allocations exceed 100%" : `${fmtPct(othersTotal)} allocated · ${fmtPct(retained)} retained`}</span>
            <span></span>
          </div>
        </div>
      </div>
      {isAdmin && <p className="text-[11px] text-[#8f8f8f] mt-3">Rows tagged <span className="text-[#bdbdbd]">Team</span> populate automatically from equity assigned in the Team tab and draw from the {poolRow?.name || "team equity pool"}. Add founders, investors, and the option pool as rows above.</p>}
    </div>
  );
}
